import { Injectable } from '@angular/core';
import { jwtDecode } from 'jwt-decode';

export interface KeycloakToken {
  exp: number;
  iat: number;
  auth_time: number;
  jti: string;
  iss: string;
  aud: string;
  sub: string;
  typ: string;
  azp: string;
  session_state: string;
  acr: string;
  'allowed-origins': string[];
  realm_access: {
    roles: string[];
  };
  resource_access: {
    [key: string]: {
      roles: string[];
    };
  };
  scope: string;
  sid: string;
  email_verified: boolean;
  name: string;
  preferred_username: string;
  given_name: string;
  family_name: string;
  email: string;
}

@Injectable({
  providedIn: 'root'
})
export class TokenStorageService {

  /**
   * Récupère le token depuis le localStorage
   */
  getTokenFromStorage(): string | null {
    try {

      const possibleKeys = [
        'keycloakToken',
        'kcToken',
        'auth_token',
        'keycloak',
        'KEYCLOAK_TOKEN'
      ];

      for (const key of possibleKeys) {
        const token = localStorage.getItem(key);
        if (token) {
          return token;
        }
      }

      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && (key.includes('keycloak') || key.includes('token') || key.includes('auth'))) {
          const token = localStorage.getItem(key);
          if (token && this.isValidToken(token)) {
            return token;
          }
        }
      }

      return null;
    } catch (error) {

      return null;
    }
  }

  /**
   * Vérifie si le token est valide (format JWT)
   */
  private isValidToken(token: string): boolean {
    try {
      // Un token JWT valide a 3 parties séparées par des points
      const parts = token.split('.');
      return parts.length === 3;
    } catch {
      return false;
    }
  }

  /**
   * Décode le token JWT
   */
  decodeToken(token: string): KeycloakToken | null {
    try {
      return jwtDecode<KeycloakToken>(token);
    } catch (error) {

      return null;
    }
  }

  /**
   * Vérifie si le token est expiré
   */
  isTokenExpired(token: string): boolean {
    try {
      const decoded = this.decodeToken(token);
      if (!decoded || !decoded.exp) return true;

      const now = Date.now() / 1000;
      return decoded.exp < now;
    } catch {
      return true;
    }
  }

  /**
   * Récupère tous les rôles du token
   */
  getRolesFromToken(token: string): string[] {
    try {
      const decoded = this.decodeToken(token);
      if (!decoded) return [];

      const roles: string[] = [];

      // Rôles du realm
      if (decoded.realm_access?.roles) {
        roles.push(...decoded.realm_access.roles);
      }

      // Rôles des resources (clients)
      if (decoded.resource_access) {
        Object.values(decoded.resource_access).forEach(resource => {
          if (resource?.roles) {
            roles.push(...resource.roles);
          }
        });
      }

      return [...new Set(roles)]; // Supprime les doublons
    } catch (error) {
      return [];
    }
  }
}